import { pageAt } from "../content";
import { base, routePath } from "./links";
import { pageMetadata } from "./page-metadata";

function updateHead(page) {
  const meta = pageMetadata(page);
  document.title = meta.title;
  document.querySelector('meta[name="description"]').content = meta.description;
  document.querySelector('link[rel="canonical"]').href = meta.canonical;
  for (const [name, value] of Object.entries({
    "og:title": meta.title,
    "og:description": meta.ogDescription,
    "og:url": meta.canonical,
    "og:image": meta.image,
  })) {
    let tag = document.querySelector(`meta[property="${name}"]`);
    if (!value) {
      tag?.remove();
      continue;
    }
    if (!tag) {
      tag = document.createElement("meta");
      tag.setAttribute("property", name);
      document.head.append(tag);
    }
    tag.content = value;
  }
}

/** Progressive enhancement: real anchors and prerendered pages remain usable. */
export function installNavigation(render) {
  const scrollMode = history.scrollRestoration;
  const positions = new Map();
  const entryKey = "xeratecNavigation";
  let currentPath = routePath(location.pathname);
  const identifyEntry = () => {
    if (!history.state?.[entryKey])
      history.replaceState(
        { ...history.state, [entryKey]: crypto.randomUUID() },
        "",
      );
    return history.state[entryKey];
  };
  let currentEntry = identifyEntry();
  const rememberScroll = () => {
    // A page's own hash filter may replaceState without retaining our key.
    currentEntry = identifyEntry();
    positions.set(currentEntry, { top: scrollY, left: scrollX });
  };
  rememberScroll();
  history.scrollRestoration = "manual";
  const pageFor = (url) => {
    if (url.origin !== location.origin || !url.pathname.startsWith(base))
      return;
    try {
      return pageAt(routePath(url.pathname));
    } catch {
      // Leave malformed URLs to the browser.
    }
  };
  const show = (page) => {
    render(page.path);
    currentPath = page.path;
    // The Vite dev template has no canonical/OG tags. Static builds do.
    if (document.querySelector('link[rel="canonical"]')) updateHead(page);
    document.getElementById("main").focus({ preventScroll: true });
  };
  const onClick = (event) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    )
      return;
    const link = event.target.closest?.("a[href]");
    if (
      !link ||
      link.hasAttribute("download") ||
      (link.target && link.target !== "_self") ||
      link.relList.contains("external")
    )
      return;
    const url = new URL(link.href);
    const page = pageFor(url);
    if (!page) return;
    // Keep same-page anchors native (including Back/Forward and hashchange).
    if (
      url.pathname === location.pathname &&
      url.search === location.search &&
      url.hash
    ) {
      rememberScroll();
      return;
    }
    event.preventDefault();
    rememberScroll();
    if (url.href !== location.href) {
      history.pushState({ [entryKey]: crypto.randomUUID() }, "", url);
      currentEntry = identifyEntry();
    }
    show(page);
    let anchor;
    try {
      anchor = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    } catch {
      // An invalid fragment still navigates to the page.
    }
    if (anchor) anchor.scrollIntoView({ behavior: "instant" });
    else window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    rememberScroll();
  };
  const onPopState = () => {
    currentEntry = identifyEntry();
    const page = pageFor(new URL(location.href));
    if (!page) {
      location.reload();
      return;
    }
    // Some browsers also fire popstate when a native hash link creates a new
    // entry. Its first anchor scroll must not be replaced with our default.
    if (page.path === currentPath && !positions.has(currentEntry)) return;
    if (page.path !== currentPath) show(page);
    window.scrollTo({
      ...(positions.get(currentEntry) || { top: 0, left: 0 }),
      behavior: "instant",
    });
  };
  const onHashChange = () => {
    // Native same-page anchors create their own history entries.
    currentEntry = identifyEntry();
    rememberScroll();
  };
  const onPageHide = () => {
    rememberScroll();
    history.scrollRestoration = scrollMode;
  };
  const onPageShow = () => {
    history.scrollRestoration = "manual";
  };
  document.addEventListener("click", onClick);
  window.addEventListener("popstate", onPopState);
  window.addEventListener("hashchange", onHashChange);
  window.addEventListener("scroll", rememberScroll, { passive: true });
  window.addEventListener("pagehide", onPageHide);
  window.addEventListener("pageshow", onPageShow);
  return () => {
    document.removeEventListener("click", onClick);
    window.removeEventListener("popstate", onPopState);
    window.removeEventListener("hashchange", onHashChange);
    window.removeEventListener("scroll", rememberScroll);
    window.removeEventListener("pagehide", onPageHide);
    window.removeEventListener("pageshow", onPageShow);
    history.scrollRestoration = scrollMode;
  };
}
